import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Per-route scroll position memory + restore-on-back behavior.
 *
 * React Router v5 doesn't ship a scroll-restoration component (that landed
 * in v6's `<ScrollRestoration>`). On a marketplace SPA the browser's own
 * auto-restore is unreliable because routes mount asynchronously, so the
 * browser tries to restore before the destination page has rendered. We
 * disable browser auto-restore and roll our own.
 *
 * Behavior:
 *   - PUSH (forward navigation, e.g. category → listing): save scrollY for
 *     the page being left, scroll to 0 on the new page (unless there's a
 *     hash, in which case we leave the browser to handle anchor scrolling).
 *   - POP (browser back/forward): restore the saved scrollY for the
 *     destination page if we have one; otherwise scroll to 0.
 *   - REPLACE: don't touch scroll. URL changes (filter toggles etc.)
 *     shouldn't unexpectedly scroll the page.
 *
 * Designed so that browsing flow on mobile feels native: tap a kit from
 * the category grid → scroll the kit page → tap browser back → land on
 * the category grid in the same scroll position. Required because mobile
 * Phase 1 dropped the inline "similar listings" section, which made the
 * back button the primary way to return to the grid.
 *
 * Storage: sessionStorage keyed on pathname+search. Cleared automatically
 * when the tab closes. Hash is stripped from the key so #anchor variants
 * of the same page share scroll history.
 */
const SCROLL_STORAGE_KEY = 'merrily.scroll-positions';

const isBrowser = () => typeof window !== 'undefined';

const keyFor = location => `${location.pathname}${location.search}`;

const getStored = () => {
  if (!isBrowser()) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(SCROLL_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeStored = positions => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // sessionStorage quota or disabled — silent fail is fine, scroll
    // restoration is a UX nicety, not a correctness requirement.
  }
};

const ScrollRestoration = () => {
  const history = useHistory();
  const location = useLocation();
  const prevKeyRef = useRef(isBrowser() ? keyFor(location) : null);
  const isFirstRenderRef = useRef(true);

  // Disable browser auto-restore once on mount.
  useEffect(() => {
    if (!isBrowser()) return;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!isBrowser()) return;

    if (isFirstRenderRef.current) {
      // Don't run save/restore on the very first render -- the page is
      // arriving fresh and the browser already placed us at the right
      // position (top, or hash anchor if present).
      isFirstRenderRef.current = false;
      prevKeyRef.current = keyFor(location);
      return;
    }

    const action = history.action;
    const newKey = keyFor(location);
    const prevKey = prevKeyRef.current;

    if (action === 'POP') {
      // Back/forward -- restore the scroll position we last saw on this page.
      const positions = getStored();
      const saved = typeof positions[newKey] === 'number' ? positions[newKey] : 0;
      // rAF gives the destination route's mount effects a chance to run
      // (they often modify content height). Without it, scrollY can land
      // past document height and snap back to 0.
      window.requestAnimationFrame(() => {
        window.scrollTo(0, saved);
      });
    } else if (action === 'PUSH') {
      // Forward nav: save where we were on the page we're leaving, then
      // scroll the new page to top -- unless there's a hash anchor, in
      // which case the browser handles it.
      const positions = getStored();
      positions[prevKey] = window.scrollY;
      writeStored(positions);
      if (!location.hash) {
        window.scrollTo(0, 0);
      }
    }
    // REPLACE: leave scroll alone. Filter changes / URL tweaks shouldn't
    // jolt the page.

    prevKeyRef.current = newKey;
  }, [location, history]);

  return null;
};

export default ScrollRestoration;
